import { useQuery } from '@apollo/client';
import { useI18n } from '@i18n';
import {
  Autocomplete,
  Button,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
} from '@mui/material';
import { getSupportedSourceTypesForSystemType } from '@predium/client-lookup';
import {
  area_type_enum,
  consumption_type_enum,
  energy_source_type_enum,
  invoice_payer_type_enum,
} from '@predium/enums';
import { translateInvoicePayerTypeEnum } from '@predium/i18n/client';
import { getValueAreaByType } from '@predium/utils';
import isNil from 'lodash/isNil';
import uniq from 'lodash/uniq';
import { useEffect, useState } from 'react';
import { Controller, useFieldArray, useFormContext } from 'react-hook-form';
import { useParams } from 'react-router';
import { ICONS } from '@/assets/icons';
import { PTypography } from '@/components/atoms/Typography/PTypography';
import { RHFDatePicker, RHFSelect, RHFTextField } from '@/components/hook-form';
import Iconify from '@/components/Iconify';
import Label from '@/components/Label';
import { useSession } from '@/components/session/useSession';
import { GET_BUILDING_CONSUMPTION_PRECONDITION } from '@/graphql/DataCollection.queries';
import { AREA_TYPE_ORDER, AreaTypeSelect } from '@/sections/components/consumption-location/AreaTypeSelect';
import { TenantAreaAutocomplete } from '@/sections/components/consumption-location/TenantAreaAutocomplete';
import { toWritableBuildingOptions } from '@/sections/components/consumption-location/writableBuildingOptions';
import SingleConsumptionDraftBox from '@/sections/DataCollection/Building/Consumption/SingleConsumptionDraftBox';
import { ConsumptionInvoiceDraftFormData } from '@/sections/DataCollection/Building/Drafts/AreaConsumptionDraftForm';

type Props = {
  disabled?: boolean;
  isSingleConsumption?: boolean;
};

const AreaConsumptionInvoiceDraft = ({ disabled = false, isSingleConsumption = false }: Props) => {
  const { t } = useI18n();
  const { org } = useSession();

  const [selectedAreaType, setSelectedAreaType] = useState<area_type_enum>();

  const { data, loading: preconditionLoading } = useQuery(GET_BUILDING_CONSUMPTION_PRECONDITION, {
    variables: {
      orgId: org.id,
    },
  });

  const { watch, control, setValue, getValues, formState, resetField } =
    useFormContext<ConsumptionInvoiceDraftFormData>();
  const { fields, append, remove } = useFieldArray<ConsumptionInvoiceDraftFormData>({
    control,
    name: 'consumptionDrafts',
  });

  const currentAreaIds = watch('areaIds');
  const currentBuildingId = watch('buildingId');

  const { id: buildingIdSource } = useParams();
  const selectedBuildingId = Number(buildingIdSource);

  const building = data?.building.find(({ id }) => id === currentBuildingId);
  const commonAreaValue = getValueAreaByType(building?.areas ?? [], area_type_enum.AF) ?? 0;

  // Initializes the area type select with the correct state if not already set
  useEffect(() => {
    if (currentAreaIds && currentAreaIds.length > 0 && !selectedAreaType) {
      const areaTypeSelected = data?.building
        .find(({ id }) => id === currentBuildingId)
        ?.areas.find(({ id }) => currentAreaIds.includes(id))?.area_type_id;

      if (areaTypeSelected) {
        setSelectedAreaType(areaTypeSelected);
      }
    }
  }, [currentAreaIds, currentBuildingId, data, selectedAreaType]);

  const currentEnergySystems =
    data?.building
      .find(({ id }) => id === currentBuildingId)
      ?.building_models.flatMap(({ energy_systems }) => energy_systems) ?? [];

  const supportedEnergySourcesForEnergySystems = uniq(
    currentEnergySystems.flatMap(({ energy_system_type_id }) =>
      getSupportedSourceTypesForSystemType(energy_system_type_id!),
    ),
  );

  // This form models "no areas" as undefined rather than an empty array.
  const setAreaIds = (areaIds: number[]) => {
    setValue('areaIds', areaIds.length > 0 ? areaIds : undefined, {
      shouldValidate: areaIds.length > 0,
      shouldDirty: true,
    });
  };

  const handleAreaTypeChange = (areaType: area_type_enum) => {
    setSelectedAreaType(areaType);

    if (areaType === area_type_enum.MF) {
      setValue('areaIds', undefined, { shouldDirty: true, shouldValidate: true });
      return;
    }

    const areaId = building?.areas.find(({ area_type_id }) => area_type_id === areaType)?.id;
    setValue('areaIds', isNil(areaId) ? undefined : [areaId], { shouldDirty: true, shouldValidate: true });
  };

  const areaTypeError = !isNil(formState.errors.areaIds) && selectedAreaType !== area_type_enum.MF;

  const from = watch('from');
  const to = watch('to');
  // used for custom behavior
  const firstConsumptionIsEmpty = !watch(`consumptionDrafts.0.consumptionTypeId`);

  const autocompleteOptions = toWritableBuildingOptions(data?.building ?? []);

  return (
    <Stack>
      <Grid container spacing={4}>
        <Grid item xs={12}>
          <PTypography variant="h6" sx={{ mb: 2 }}>
            {t('DataCollection_ConsumptionDraftLocationInput-label')}
          </PTypography>

          <Autocomplete
            // TODO: Changing buildings is not yet supported on a single consumption
            disabled={disabled || isSingleConsumption}
            disableClearable
            style={{ flexGrow: 1, marginBottom: 24 }}
            options={autocompleteOptions}
            // @ts-ignore
            value={autocompleteOptions.find((option) => option.value === getValues('buildingId')) ?? null}
            onChange={(_, chosenOption) => {
              const chosenValue = chosenOption as unknown as (typeof autocompleteOptions)[number];

              setValue('buildingId', chosenValue.value, { shouldValidate: true, shouldDirty: true });
              setValue('areaIds', undefined as any);
              setSelectedAreaType(undefined);
            }}
            renderOption={(props, option) => (
              <MenuItem {...props} key={option.value} style={{ display: 'flex' }}>
                <span style={{ flexGrow: 1, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                  {option.label}
                </span>
                {option.value === selectedBuildingId && (
                  <Tooltip title={t('DataCollectionAreaConsumptionInvoiceDraft_CurrentBuilding')} arrow placement="top">
                    <Label ml={1}>{t('DataCollectionAreaConsumptionInvoiceDraft_CurrentBuilding')}</Label>
                  </Tooltip>
                )}
              </MenuItem>
            )}
            renderInput={(params) => (
              <Controller
                name="buildingId"
                control={control}
                render={({ fieldState: { error }, field: { ref } }) => {
                  return (
                    <TextField
                      {...params}
                      error={Boolean(error)}
                      InputProps={{
                        ...params.InputProps,
                        endAdornment: formState.dirtyFields.buildingId ? (
                          <>
                            <InputAdornment position="end">
                              <Tooltip
                                arrow
                                placement="top"
                                title={t('DataCollection_ConsumptionAssociatedBuildingSelectReset-tooltip')}
                              >
                                <IconButton
                                  size="small"
                                  onClick={() => resetField('buildingId')}
                                  disabled={disabled || isSingleConsumption}
                                >
                                  <Iconify icon={ICONS.ICON_PARK_OUTLINE_RETURN} />
                                </IconButton>
                              </Tooltip>
                            </InputAdornment>

                            {params.InputProps.endAdornment}
                          </>
                        ) : (
                          params.InputProps.endAdornment
                        ),
                      }}
                      inputRef={ref}
                      helperText={
                        error
                          ? error?.message
                          : formState.dirtyFields.buildingId
                            ? t('DataCollection_ConsumptionAssociatedBuildingSelect-valueChangedHelperText')
                            : undefined
                      }
                      label={t('DataCollection_ConsumptionAssociatedBuildingSelect-label')}
                    />
                  );
                }}
              />
            )}
          />

          <AreaTypeSelect
            sx={{ mb: 3 }}
            value={selectedAreaType ?? ''}
            onChange={handleAreaTypeChange}
            areaTypes={AREA_TYPE_ORDER.filter((areaType) => !(areaType === area_type_enum.AF && commonAreaValue === 0))}
            disabled={disabled}
            loading={preconditionLoading}
            error={areaTypeError}
            helperText={areaTypeError ? formState.errors.areaIds?.message : undefined}
          />

          {selectedAreaType === area_type_enum.MF ? (
            <Controller
              name="areaIds"
              control={control}
              render={({ fieldState: { error }, field: { ref } }) => (
                <TenantAreaAutocomplete
                  sx={{ flexGrow: 1 }}
                  areas={building?.areas ?? []}
                  value={currentAreaIds ?? []}
                  onChange={setAreaIds}
                  disabled={disabled}
                  inputRef={ref}
                  error={!isNil(error)}
                  helperText={
                    !isNil(error)
                      ? error.message
                      : formState.dirtyFields.areaIds
                        ? t('DataCollection_ConsumptionAssociatedTenantAreaSelect-valueChangedHelperText')
                        : // Rendering an empty string keeps the options list from being mispositioned once an error appears.
                          ' '
                  }
                  listboxActions={
                    formState.isDirty ? (
                      <Button type="button" variant="text" size="small" onClick={() => resetField('areaIds')}>
                        {t('DataCollection_ConsumptionAssociatedTenantAreaSelect-RestoreAreasSelection')}
                      </Button>
                    ) : undefined
                  }
                />
              )}
            />
          ) : null}
        </Grid>

        <Grid
          item
          xs={12}
          // As we now are always rendering the helperText, we correct the spacing a little bit.
          sx={{ '&.MuiGrid-item': { marginTop: selectedAreaType === area_type_enum.MF ? 0 : -3 } }}
        >
          <PTypography variant="h6" sx={{ mb: 2 }}>
            {t('DataCollection_ConsumptionDraftGeneralData-title')}
          </PTypography>
          <RHFTextField
            label={t('General_Provider')}
            name="provider"
            state={disabled ? 'disabled' : 'enabled'}
            sx={{ mb: 2 }}
          />

          <Grid container spacing={2}>
            {!isSingleConsumption && (
              <>
                <Grid item xs={6}>
                  <RHFDatePicker
                    label={t('General_InvoiceDate')}
                    name="invoiceDate"
                    state={disabled ? 'disabled' : 'enabled'}
                  />
                </Grid>

                <Grid item xs={6}>
                  <RHFSelect name="invoicePayer" label={t('General_Paid_by')} state={disabled ? 'disabled' : 'enabled'}>
                    {Object.values(invoice_payer_type_enum).map((option) => (
                      <MenuItem key={option} value={option}>
                        {/* TODO: Add proper icon later */}

                        {translateInvoicePayerTypeEnum(option)}
                      </MenuItem>
                    ))}
                  </RHFSelect>
                </Grid>
              </>
            )}

            <Grid item xs={6}>
              <RHFDatePicker
                maxDate={to as any}
                label={t('General_FromTo-from')}
                name="from"
                state={disabled ? 'disabled' : 'enabled'}
              />
            </Grid>
            <Grid item xs={6}>
              <RHFDatePicker
                minDate={from as any}
                label={t('General_FromTo-to')}
                name="to"
                state={disabled ? 'disabled' : 'enabled'}
              />
            </Grid>
          </Grid>
        </Grid>

        <Grid item xs={12}>
          <PTypography variant="h6" sx={{ mb: 2 }}>
            {t('DataCollection_ConsumptionDraftData-title')}
          </PTypography>

          <Stack gap={2}>
            {fields.map(({ id }, index) => (
              <SingleConsumptionDraftBox
                id={id}
                key={id}
                index={index}
                remove={() => {
                  if (fields.length > 1) {
                    remove(index);
                  }
                  // Just erase the values
                  else {
                    setValue(`consumptionDrafts.0.consumptionTypeId`, undefined as any);
                    setValue(`consumptionDrafts.0.energySourceTypeId`, undefined);
                    setValue(`consumptionDrafts.0.subTypeId`, undefined);
                    setValue(`consumptionDrafts.0.value`, undefined);
                    setValue(`consumptionDrafts.0.displayUnitValue`, undefined);
                    setValue(`consumptionDrafts.0.cost`, undefined);
                    setValue(`consumptionDrafts.0.waste`, undefined);
                  }
                }}
                disabled={disabled}
                isSingleConsumption={isSingleConsumption}
                limitEnergySourcesBy={supportedEnergySourcesForEnergySystems}
              />
            ))}
          </Stack>

          {!isSingleConsumption && !firstConsumptionIsEmpty && (
            <Button
              variant="text"
              startIcon={<Iconify icon={ICONS.PLUS} />}
              sx={{ mt: 2 }}
              disabled={disabled}
              onClick={() => {
                // Add empty consumption draft
                append({
                  value: undefined as unknown as number,
                  cost: undefined as unknown as number,
                  consumptionTypeId: undefined as unknown as consumption_type_enum,
                  energySourceTypeId: undefined as unknown as energy_source_type_enum,
                });
              }}
            >
              {t('General_Add')}
            </Button>
          )}
        </Grid>
      </Grid>
    </Stack>
  );
};

export default AreaConsumptionInvoiceDraft;
